import { Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, redirect } from 'react-router-dom';
import type { LoaderFunctionArgs } from 'react-router-dom';

import { usePageTracking } from '@/shared/hooks/use-page-tracking';

// Eagerly load auth pages for better UX
import { AuthCallbackPage } from '@/pages/auth/callback';
import { RegisterPage } from '@/pages/auth/register';

import { AuthLayout } from '@/widgets/layouts/auth-layout';
import { BaseLayout } from '@/widgets/layouts/base-layout';

import { ROUTES } from '@/shared/config/routes';

import { RouteErrorBoundary } from '@/shared/components/route-error-boundary';
import { lazyWithRetry } from '@/shared/lib/lazy-with-retry';

import { Permission, ProtectedRoute } from './protected-route';

// Lazy load all pages — lazyWithRetry auto-recovers from stale-chunk errors
// after a deploy by triggering one full-page reload at the import layer.
const NotFoundPage = lazyWithRetry(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);
const ProfilePage = lazyWithRetry(() => import('@/pages/profile').then((m) => ({ default: m.ProfilePage })));

const DashboardLayout = lazyWithRetry(() => import('@/widgets/layouts/dashboard-layout').then(m => ({ default: m.DashboardLayout })));
const PlaygroundPage = lazyWithRetry(() => import('@/pages/dashboard/playground').then(m => ({ default: m.PlaygroundPage })));
const QuickstartPage = lazyWithRetry(() => import('@/pages/dashboard/quickstart').then(m => ({ default: m.QuickstartPage })));
const CharactersPage = lazyWithRetry(() => import('@/pages/dashboard/characters').then(m => ({ default: m.CharactersPage })));
const CharacterDetailPage = lazyWithRetry(() => import('@/pages/dashboard/characters/detail').then(m => ({ default: m.CharacterDetailPage })));
const StoriesLayout = lazyWithRetry(() => import('@/pages/dashboard/stories/layout').then(m => ({ default: m.StoriesLayout })));
const StoriesPage = lazyWithRetry(() => import('@/pages/dashboard/stories').then(m => ({ default: m.StoriesPage })));
const StoryPage = lazyWithRetry(() => import('@/pages/dashboard/stories/story').then(m => ({ default: m.StoryPage })));
const StoryEpisodesPage = lazyWithRetry(() => import('@/pages/dashboard/stories/episodes').then(m => ({ default: m.StoryEpisodesPage })));
const StoryEpisodePage = lazyWithRetry(() => import('@/pages/dashboard/stories/episode').then(m => ({ default: m.StoryEpisodePage })));
const AdsPage = lazyWithRetry(() => import('@/pages/dashboard/ads').then(m => ({ default: m.AdsPage })));
const AdProjectPage = lazyWithRetry(() => import('@/pages/dashboard/ads/project').then(m => ({ default: m.AdProjectPage })));
const SlideshowsLayout = lazyWithRetry(() => import('@/pages/dashboard/slideshows/layout').then(m => ({ default: m.SlideshowsLayout })));
const SlideshowsPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows').then(m => ({ default: m.SlideshowsPage })));
const SlideshowPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows/slideshow').then(m => ({ default: m.SlideshowPage })));
const SlideshowsSettingsPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows/settings').then(m => ({ default: m.SlideshowsSettingsPage })));
const SlideshowPushesPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows/pushes').then(m => ({ default: m.SlideshowPushesPage })));
const SlideshowPushPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows/push').then(m => ({ default: m.SlideshowPushPage })));
const SlideshowNewPushPage = lazyWithRetry(() => import('@/pages/dashboard/slideshows/push-new').then(m => ({ default: m.SlideshowNewPushPage })));
const CarouselsLayout = lazyWithRetry(() => import('@/pages/dashboard/slideshows/layout').then(m => ({ default: m.CarouselsLayout })));
const CarouselsPage = lazyWithRetry(() => import('@/pages/dashboard/carousels').then(m => ({ default: m.CarouselsPage })));
const CarouselPage = lazyWithRetry(() => import('@/pages/dashboard/carousels/carousel').then(m => ({ default: m.CarouselPage })));
const PosterPage = lazyWithRetry(() => import('@/pages/poster').then(m => ({ default: m.PosterPage })));
const LorasPage = lazyWithRetry(() => import('@/pages/dashboard/loras').then(m => ({ default: m.LorasPage })));
const TeamPage = lazyWithRetry(() => import('@/pages/dashboard/team').then(m => ({ default: m.TeamPage })));
const ApiKeysPage = lazyWithRetry(() => import('@/pages/dashboard/api-keys').then(m => ({ default: m.ApiKeysPage })));
const WebhooksPage = lazyWithRetry(() => import('@/pages/dashboard/webhooks').then(m => ({ default: m.WebhooksPage })));
const UsagePage = lazyWithRetry(() => import('@/pages/dashboard/usage').then(m => ({ default: m.UsagePage })));
const BillingPage = lazyWithRetry(() => import('@/pages/dashboard/billing').then(m => ({ default: m.BillingPage })));
const AdminUsagePage = lazyWithRetry(() => import('@/pages/admin/usage').then(m => ({ default: m.AdminUsagePage })));
const AdminTeamsPage = lazyWithRetry(() => import('@/pages/admin/teams').then(m => ({ default: m.AdminTeamsPage })));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/users').then(m => ({ default: m.AdminUsersPage })));
const AdminLorasPage = lazyWithRetry(() => import('@/pages/admin/loras').then(m => ({ default: m.AdminLorasPage })));
const AdminLoraDetailPage = lazyWithRetry(() => import('@/pages/admin/loras/detail').then(m => ({ default: m.AdminLoraDetailPage })));
const AdminActionsPage = lazyWithRetry(() => import('@/pages/admin/actions').then(m => ({ default: m.AdminActionsPage })));
const AdminActionDetailPage = lazyWithRetry(() => import('@/pages/admin/actions/detail').then(m => ({ default: m.AdminActionDetailPage })));
const AdminCharactersPage = lazyWithRetry(() => import('@/pages/admin/characters').then(m => ({ default: m.AdminCharactersPage })));
const PackagesPage = lazyWithRetry(() => import('@/pages/dashboard/packages').then(m => ({ default: m.PackagesPage })));
const NewPackagePage = lazyWithRetry(() => import('@/pages/dashboard/packages/new-package').then(m => ({ default: m.NewPackagePage })));
const PackagePage = lazyWithRetry(() => import('@/pages/dashboard/packages/package-page').then(m => ({ default: m.PackagePage })));
const AdminStoriesPage = lazyWithRetry(() => import('@/pages/admin/stories').then(m => ({ default: m.AdminStoriesPage })));
const AdminCharacterSettingsPage = lazyWithRetry(() => import('@/pages/admin/character-settings').then(m => ({ default: m.AdminCharacterSettingsPage })));
const AdminAuditPage = lazyWithRetry(() => import('@/pages/admin/audit').then(m => ({ default: m.AdminAuditPage })));
const AdminInvoicesPage = lazyWithRetry(() => import('@/pages/admin/invoices').then(m => ({ default: m.AdminInvoicesPage })));
const AdminPricingPage = lazyWithRetry(() => import('@/pages/admin/pricing').then(m => ({ default: m.AdminPricingPage })));
const AdminTemplatesPage = lazyWithRetry(() => import('@/pages/admin/templates').then(m => ({ default: m.AdminTemplatesPage })));
const AdminMonitoringPage = lazyWithRetry(() => import('@/pages/admin/monitoring').then(m => ({ default: m.AdminMonitoringPage })));
const AdminDispatcherPage = lazyWithRetry(() => import('@/pages/admin/dispatcher').then(m => ({ default: m.AdminDispatcherPage })));
const AdminInferencesPage = lazyWithRetry(() => import('@/pages/admin/inferences').then(m => ({ default: m.AdminInferencesPage })));
const AdminStorageHealthPage = lazyWithRetry(() => import('@/pages/admin/storage-health').then(m => ({ default: m.AdminStorageHealthPage })));
const AdminModerationPage = lazyWithRetry(() => import('@/pages/admin/moderation').then(m => ({ default: m.AdminModerationPage })));
const AdminLlmModelsPage = lazyWithRetry(() => import('@/pages/admin/models').then(m => ({ default: m.AdminLlmModelsPage })));
const AdminLlmModelNewPage = lazyWithRetry(() => import('@/pages/admin/models/new').then(m => ({ default: m.AdminLlmModelNewPage })));
const AdminLlmModelDetailPage = lazyWithRetry(() => import('@/pages/admin/models/detail').then(m => ({ default: m.AdminLlmModelDetailPage })));
const AdminLlmAnchorPage = lazyWithRetry(() => import('@/pages/admin/pricing/anchor').then(m => ({ default: m.AdminLlmAnchorPage })));
// Team detail sub-routes (Phase 8) — tabbed shell under /admin/teams/:id.
const AdminTeamDetailLayout = lazyWithRetry(() => import('@/pages/admin/teams/[id]/_components/team-detail-layout').then(m => ({ default: m.TeamDetailLayout })));
const AdminTeamOverviewPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/overview').then(m => ({ default: m.TeamOverviewPage })));
const AdminTeamMembersPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/members').then(m => ({ default: m.TeamMembersPage })));
const AdminTeamPricingPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/pricing').then(m => ({ default: m.TeamPricingPage })));
const AdminTeamApiKeysPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/api-keys').then(m => ({ default: m.TeamApiKeysPage })));
const AdminTeamUsagePage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/usage').then(m => ({ default: m.TeamUsagePage })));
const AdminTeamAuditPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/audit').then(m => ({ default: m.TeamAuditPage })));
const AdminTeamContentPage = lazyWithRetry(() => import('@/pages/admin/teams/[id]/content').then(m => ({ default: m.AdminTeamContentPage })));

const OnboardingPage = lazyWithRetry(() => import('@/pages/onboarding').then(m => ({ default: m.OnboardingPage })));
const PendingPage = lazyWithRetry(() => import('@/pages/pending'));
const InvitePage = lazyWithRetry(() => import('@/pages/invite').then(m => ({ default: m.InvitePage })));
// DEV-only visual harness + ghost-mode door (referenced only inside an
// import.meta.env.DEV branch below, so consts + chunks are tree-shaken out
// of production builds).
const PendingDevHarness = lazyWithRetry(() => import('@/features/pending/dev-harness').then(m => ({ default: m.PendingDevHarness })));
const GhostEnterPage = lazyWithRetry(() => import('@/features/dev/ghost-enter').then(m => ({ default: m.GhostEnterPage })));

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

// Root layout that provides router context for hooks like usePageTracking
const RootLayout = () => {
  usePageTracking();
  return <Outlet />;
};

/**
 * Review batch (2026-07-14), Item 1 — reachability fix: only the query-style
 * `/admin/inferences?id=<id>` route rendered the detail view; a path-style
 * `/admin/inferences/<id>` (e.g. the usage drawer's "View full admin record →"
 * link, or any pasted path-style URL) hit the SPA 404. Redirect path-style →
 * query-style, preserving the id. Exported so it's unit-testable without
 * mounting the full route tree.
 */
export function inferenceDetailRedirectLoader({ params }: LoaderFunctionArgs) {
  return redirect(`/admin/inferences?id=${params.id}`);
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // A TikTok Slideshows poster's personal link: public, no sign-in, no dashboard chrome.
      { path: 'p/:token', element: <Suspense fallback={<PageLoader />}><PosterPage /></Suspense> },
      // OnboardingLayout has no sidebar — must NOT be a child of BaseLayout, otherwise
      // the dashboard chrome leaks into the wizard and users can wander out mid-funnel.
      //
      // Intentionally NOT gated by Permission.APPROVED. The funnel is
      //   signup → onboarding (name team + wizard) → pending (await approval) → dashboard.
      // A brand-new user is `pending` by default per the closed-beta gate;
      // gating /onboarding on APPROVED would bounce them to /pending before
      // they ever see the wizard, leaving teams stuck on their auto-generated
      // "{display}'s Workspace" name. Dashboard routes are still gated, so
      // pending users still can't reach the product surface — they just get
      // to finish naming their team while they wait.
      {
        path: 'onboarding',
        element: (
          <ProtectedRoute permissions={[Permission.AUTH]}>
            <Suspense fallback={<PageLoader />}>
              <OnboardingPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      // DEV-only visual harness for the holding page (no auth, injected user;
      // ?status=rejected|suspended). Collapses to [] in production builds —
      // /dev/pending does not exist outside `vite dev`.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev/pending',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PendingDevHarness />
                </Suspense>
              ),
            },
            {
              path: 'dev/enter',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <GhostEnterPage />
                </Suspense>
              ),
            },
          ]
        : []),
      // Closed-beta holding page — only requires AUTH (NOT APPROVED, or it
      // would loop). Pending/rejected/suspended users land here from the
      // approval-gate redirect in ProtectedRoute.
      {
        path: 'pending',
        element: (
          <ProtectedRoute permissions={[Permission.AUTH]}>
            <Suspense fallback={<PageLoader />}>
              <PendingPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/',
        element: <BaseLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: 'invite',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InvitePage />
              </Suspense>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute permissions={[Permission.AUTH, Permission.APPROVED]}>
                <Suspense fallback={<PageLoader />}>
                  <DashboardLayout />
                </Suspense>
              </ProtectedRoute>
            ),
            children: [
              { index: true, element: <Suspense fallback={<PageLoader />}><PlaygroundPage /></Suspense> },
              { path: 'playground', element: <Suspense fallback={<PageLoader />}><PlaygroundPage /></Suspense> },
              { path: 'quickstart', element: <Suspense fallback={<PageLoader />}><QuickstartPage /></Suspense> },
              { path: 'characters', element: <Suspense fallback={<PageLoader />}><CharactersPage /></Suspense> },
              { path: 'characters/:id', element: <Suspense fallback={<PageLoader />}><CharacterDetailPage /></Suspense> },
              {
                // Stories: one parent route holds the feature flag and the shared scope.
                path: 'stories',
                handle: { fullWidth: true },
                element: <Suspense fallback={<PageLoader />}><StoriesLayout /></Suspense>,
                children: [
                  { index: true, element: <Suspense fallback={<PageLoader />}><StoriesPage /></Suspense> },
                  { path: ':storyId', element: <Suspense fallback={<PageLoader />}><StoryPage /></Suspense> },
                  { path: ':storyId/episodes', element: <Suspense fallback={<PageLoader />}><StoryEpisodesPage /></Suspense> },
                  { path: ':storyId/episodes/:episodeId', element: <Suspense fallback={<PageLoader />}><StoryEpisodePage /></Suspense> },
                ],
              },
              {
                // Private packages: their own pages and API, behind the same flag as Stories.
                path: 'packages',
                handle: { fullWidth: true },
                element: <Suspense fallback={<PageLoader />}><StoriesLayout /></Suspense>,
                children: [
                  { index: true, element: <Suspense fallback={<PageLoader />}><PackagesPage /></Suspense> },
                  { path: 'new', element: <Suspense fallback={<PageLoader />}><NewPackagePage /></Suspense> },
                  { path: ':packageId', element: <Suspense fallback={<PageLoader />}><PackagePage /></Suspense> },
                ],
              },
              { path: 'ads', handle: { fullWidth: true }, element: <Suspense fallback={<PageLoader />}><AdsPage /></Suspense> },
              { path: 'ads/:projectId', handle: { fullWidth: true }, element: <Suspense fallback={<PageLoader />}><AdProjectPage /></Suspense> },
              {
                // Slideshows: one parent route holds the experimental-features switch.
                path: 'slideshows',
                handle: { fullWidth: true },
                element: <Suspense fallback={<PageLoader />}><SlideshowsLayout /></Suspense>,
                children: [
                  { index: true, element: <Suspense fallback={<PageLoader />}><SlideshowsPage /></Suspense> },
                  { path: 'settings', element: <Suspense fallback={<PageLoader />}><SlideshowsSettingsPage /></Suspense> },
                  { path: 'pushes', element: <Suspense fallback={<PageLoader />}><SlideshowPushesPage /></Suspense> },
                  { path: 'pushes/new', element: <Suspense fallback={<PageLoader />}><SlideshowNewPushPage /></Suspense> },
                  { path: 'pushes/:pushId', element: <Suspense fallback={<PageLoader />}><SlideshowPushPage /></Suspense> },
                  { path: ':slideshowId', element: <Suspense fallback={<PageLoader />}><SlideshowPage /></Suspense> },
                ],
              },
              {
                // Carousels: on the same experimental-features switch as Slideshows.
                path: 'carousels',
                handle: { fullWidth: true },
                element: <Suspense fallback={<PageLoader />}><CarouselsLayout /></Suspense>,
                children: [
                  { index: true, element: <Suspense fallback={<PageLoader />}><CarouselsPage /></Suspense> },
                  { path: ':carouselId', element: <Suspense fallback={<PageLoader />}><CarouselPage /></Suspense> },
                ],
              },
              { path: 'loras', element: <Suspense fallback={<PageLoader />}><LorasPage /></Suspense> },
              { path: 'profile', element: <Suspense fallback={<PageLoader />}><ProfilePage /></Suspense> },
              { path: 'team', element: <Suspense fallback={<PageLoader />}><TeamPage /></Suspense> },
              { path: 'api-keys', element: <Suspense fallback={<PageLoader />}><ApiKeysPage /></Suspense> },
              { path: 'webhooks', element: <Suspense fallback={<PageLoader />}><WebhooksPage /></Suspense> },
              { path: 'usage', element: <Suspense fallback={<PageLoader />}><UsagePage /></Suspense> },
              { path: 'billing', element: <Suspense fallback={<PageLoader />}><BillingPage /></Suspense> },
            ],
          },
          {
            path: 'admin',
            // Route-level boundary so a render throw inside any admin page
            // degrades to the recovery UI instead of bubbling to the root and
            // blanking the whole document. (The import-layer chunk-reload path
            // in lazyWithRetry still flows through here too.)
            errorElement: <RouteErrorBoundary />,
            element: (
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <DashboardLayout />
                </Suspense>
              </ProtectedRoute>
            ),
            children: [
              { path: 'teams', element: <Suspense fallback={<PageLoader />}><AdminTeamsPage /></Suspense> },
              {
                path: 'teams/:id',
                element: <Suspense fallback={<PageLoader />}><AdminTeamDetailLayout /></Suspense>,
                children: [
                  { index: true, element: <Suspense fallback={<PageLoader />}><AdminTeamOverviewPage /></Suspense> },
                  { path: 'content', element: <Suspense fallback={<PageLoader />}><AdminTeamContentPage /></Suspense> },
                  { path: 'members', element: <Suspense fallback={<PageLoader />}><AdminTeamMembersPage /></Suspense> },
                  { path: 'pricing', element: <Suspense fallback={<PageLoader />}><AdminTeamPricingPage /></Suspense> },
                  { path: 'api-keys', element: <Suspense fallback={<PageLoader />}><AdminTeamApiKeysPage /></Suspense> },
                  { path: 'usage', element: <Suspense fallback={<PageLoader />}><AdminTeamUsagePage /></Suspense> },
                  { path: 'audit', element: <Suspense fallback={<PageLoader />}><AdminTeamAuditPage /></Suspense> },
                ],
              },
              { path: 'users', element: <Suspense fallback={<PageLoader />}><AdminUsersPage /></Suspense> },
              { path: 'loras', element: <Suspense fallback={<PageLoader />}><AdminLorasPage /></Suspense> },
              { path: 'loras/:id', element: <Suspense fallback={<PageLoader />}><AdminLoraDetailPage /></Suspense> },
              { path: 'characters', element: <Suspense fallback={<PageLoader />}><AdminCharactersPage /></Suspense> },
              { path: 'stories', element: <Suspense fallback={<PageLoader />}><AdminStoriesPage /></Suspense> },
              { path: 'character-settings', element: <Suspense fallback={<PageLoader />}><AdminCharacterSettingsPage /></Suspense> },
              { path: 'audit', element: <Suspense fallback={<PageLoader />}><AdminAuditPage /></Suspense> },
              { path: 'usage', element: <Suspense fallback={<PageLoader />}><AdminUsagePage /></Suspense> },
              { path: 'invoices', element: <Suspense fallback={<PageLoader />}><AdminInvoicesPage /></Suspense> },
              { path: 'pricing', element: <Suspense fallback={<PageLoader />}><AdminPricingPage /></Suspense> },
              { path: 'templates', element: <Suspense fallback={<PageLoader />}><AdminTemplatesPage /></Suspense> },
              { path: 'monitoring', element: <Suspense fallback={<PageLoader />}><AdminMonitoringPage /></Suspense> },
              { path: 'dispatcher', element: <Suspense fallback={<PageLoader />}><AdminDispatcherPage /></Suspense> },
              { path: 'inferences', element: <Suspense fallback={<PageLoader />}><AdminInferencesPage /></Suspense> },
              { path: 'inferences/:id', loader: inferenceDetailRedirectLoader },
              { path: 'storage-health', element: <Suspense fallback={<PageLoader />}><AdminStorageHealthPage /></Suspense> },
              { path: 'moderation', element: <Suspense fallback={<PageLoader />}><AdminModerationPage /></Suspense> },
              { path: 'models', element: <Suspense fallback={<PageLoader />}><AdminLlmModelsPage /></Suspense> },
              { path: 'models/new', element: <Suspense fallback={<PageLoader />}><AdminLlmModelNewPage /></Suspense> },
              { path: 'models/:id', element: <Suspense fallback={<PageLoader />}><AdminLlmModelDetailPage /></Suspense> },
              { path: 'pricing/anchor', element: <Suspense fallback={<PageLoader />}><AdminLlmAnchorPage /></Suspense> },
              { path: 'actions', element: <Suspense fallback={<PageLoader />}><AdminActionsPage /></Suspense> },
              { path: 'actions/:id', element: <Suspense fallback={<PageLoader />}><AdminActionDetailPage /></Suspense> },
            ],
          },
        ],
      },
      {
        path: ROUTES.AUTH.BASE,
        element: <AuthLayout />,
        children: [
          {
            path: 'register',
            element: <RegisterPage />,
          },
          {
            path: 'callback',
            element: <AuthCallbackPage />,
          },
        ],
      },
      {
        path: ROUTES.NOT_FOUND,
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export const AppRouter = () => {
  return <RouterProvider router={router} />;
};
